/**
 * The quiet page on a phone — `docs/quiet-records.md` §4 "Phone only",
 * mockup board 10 (`qr-10-phone-sheets`).
 *
 * Three surfaces, and each one exists because the desktop gesture does not
 * survive a 390px screen:
 *
 * **`QuietComposerTrigger`** — the note field on the panel is a *read-only
 * trigger*, not a text box. A `contentEditable` focused in place raises the
 * keyboard over the bottom half of the screen, which is where the six first
 * steps are: somebody tapping the field to write a note loses the page they
 * were reading. Tapping it opens the sheet instead, which owns the whole
 * screen and can afford a keyboard.
 *
 * **`QuietLogSheet`** — the same draft as the card composer, through
 * `useActivityDraft`, drawn at thumb size: six kinds as a 3 × 2 grid of 48px
 * tiles, the scheduled fields as 52px rows, the visibility control at 40px
 * and the primary in the sheet's own sticky footer.
 *
 * **`AttachSheet`** — a phone cannot drop a file and has nothing worth
 * opening blind, so the dashed tile's gesture becomes three 68px rows: the
 * camera, the photo library, and a file. The visibility answer is asked here
 * rather than guessed, because a photo taken at a gate is usually one the
 * crew should see and the describe dialog that follows is one tap further on.
 *
 * All three are the shared `PanelSheet` (v3 §6: "every panel on a phone is
 * the same bottom sheet"), and the *bodies* are ordinary components — the
 * same argument `ToolPanel` makes for the list engine's five panels.
 */
import { useEffect, useRef, useState } from 'react'
import {
  ACTIVITY_KINDS, ACTIVITY_KIND_LABELS, ACTIVITY_KIND_VERBS,
  type ActivityKind, type FileVisibility,
} from '@rowted/shared'
import { cn } from '../lib/cn.js'
import { RichNoteEditor } from '../components/note-rich-editor.js'
import { PanelSheet, SheetButton } from '../components/panel-sheet.js'
import { IconCamera, IconFile, IconImage } from '../frame/icons.js'
import { KindTile } from './record-overview.js'
import { useActivityDraft, useMentionable, type ActivitySubmit } from './record-activity.js'
import { choiceWords, MessageChoiceRows, useRecordMessage, type MessageRecord } from './record-message.js'
import type { UploadOptions } from './use-record-upload.js'

/**
 * The panel's note field on a phone: a trigger and six chips.
 *
 * The chips are not decoration — they are how somebody logging a call gets
 * to a call in one tap rather than opening the sheet and changing the kind.
 * Each one opens the sheet already on its kind.
 */
export function QuietComposerTrigger({ subject, onOpen, kinds = ACTIVITY_KINDS }: {
  subject: string
  onOpen: (kind: ActivityKind) => void
  /** The kinds this person is offered — Email and Text only with an Inbox (`record-message.tsx`). */
  kinds?: readonly ActivityKind[]
}) {
  return (
    <section className="rounded-record border border-line bg-surface p-3">
      <button
        type="button"
        onClick={() => onOpen('note')}
        className="flex h-11 w-full items-center rounded-field border border-line bg-field px-3
                   text-left text-body text-text-faint"
      >
        Write a note about {subject}…
      </button>

      <div className="no-scrollbar -mx-1 mt-2.5 flex gap-1.5 overflow-x-auto px-1">
        {kinds.map((kind) => (
          <button
            key={kind}
            type="button"
            onClick={() => onOpen(kind)}
            className="flex h-11 shrink-0 items-center gap-1.5 rounded-button border
                       border-transparent px-2.5 text-cell font-semibold text-text-muted
                       active:bg-hover"
          >
            <KindTile kind={kind} size={20} />
            {ACTIVITY_KIND_LABELS[kind]}
          </button>
        ))}
      </div>
    </section>
  )
}

/** Writing one activity, thumb-sized. */
export function QuietLogSheet({
  open, initialKind, subject, people, busy, onSubmit, onClose, record, onSent,
}: {
  open: boolean
  /** Which kind the chip that opened it named. */
  initialKind: ActivityKind
  subject: string
  people: { id: string; name: string }[]
  busy?: boolean
  onSubmit: ActivitySubmit
  onClose: () => void
  /**
   * The record — *Email* and *Text* are then for somebody with an Inbox only,
   * and ask *Send* or *Log* first (`record-message.tsx`).
   */
  record?: MessageRecord
  onSent?: () => void
}) {
  const draft = useActivityDraft(onSubmit)
  const { setKind } = draft
  const mentionable = useMentionable(people)
  const message = useRecordMessage(record, onSent)
  /*
   * *Email* and *Text* ask first, in this sheet's own body — a level, never a
   * second sheet over this one (v3 §6.4): *Send* swaps the sheet for Activity's
   * New email or New text, *Log* comes back here on that kind (Aaron, Oct 24).
   */
  const [choosing, setChoosing] = useState<'email' | 'sms' | null>(null)
  const pick = (next: ActivityKind) => {
    if (record && (next === 'email' || next === 'sms')) {
      if (message.canSend !== false) setChoosing(next)
      return
    }
    setKind(next)
  }
  useEffect(() => { if (!open) setChoosing(null) }, [open])

  /*
   * The chip that opened the sheet decides the kind — and only on the way in.
   * Watching `initialKind` for ever would undo somebody's own change the next
   * time anything above re-rendered, so this runs on `open`.
   */
  useEffect(() => { if (open) pick(initialKind) }, [open, initialKind, setKind])

  if (choosing) {
    return (
      <>
      {message.sheet}
      <PanelSheet open={open} onClose={onClose} title={choiceWords(choosing).title} sub={subject}
        onBack={() => setChoosing(null)} backLabel="Log activity">
        <MessageChoiceRows
          channel={choosing}
          phone
          onSend={() => { setChoosing(null); onClose(); message.send(choosing) }}
          onLog={() => { setChoosing(null); setKind(choosing) }}
        />
      </PanelSheet>
      </>
    )
  }

  return (
    <>
    {message.sheet}
    <PanelSheet
      open={open}
      onClose={onClose}
      title="Log activity"
      sub={subject}
      footer={(
        <SheetButton
          disabled={!draft.ready || busy}
          onClick={() => void draft.submit().then(onClose)}
        >
          {busy ? 'Saving…' : ACTIVITY_KIND_VERBS[draft.kind]}
        </SheetButton>
      )}
    >
      <div className="flex flex-col gap-3 p-4">
        {/* Six kinds, 3 × 2, 48px high — a thumb's target, not a chip row. */}
        <div role="radiogroup" aria-label="What kind" className="grid grid-cols-3 gap-1.5">
          {message.kinds.map((kind) => (
            <button
              key={kind}
              type="button"
              role="radio"
              aria-checked={draft.kind === kind}
              onClick={() => pick(kind)}
              className={cn(
                'flex h-12 items-center justify-center gap-1.5 rounded-button border',
                'text-cell font-semibold',
                draft.kind === kind
                  ? 'border-accent-line bg-accent-soft text-accent-dark'
                  : 'border-line bg-surface text-text-muted',
              )}
            >
              <KindTile kind={kind} size={20} />
              {ACTIVITY_KIND_LABELS[kind]}
            </button>
          ))}
        </div>

        {draft.scheduled && (
          <input
            value={draft.title}
            onChange={(event) => draft.setTitle(event.target.value)}
            placeholder={draft.kind === 'task' ? 'What needs doing?' : 'What is the meeting?'}
            className="h-[52px] w-full rounded-field border border-line bg-surface px-3
                       text-body text-text placeholder:text-text-faint"
          />
        )}

        {/* The card composer's rich box (Aaron, Oct 25), at a phone's size — @ names somebody here too. */}
        <RichNoteEditor
          label="Note"
          value={draft.body}
          onChange={draft.setBody}
          disabled={busy}
          minHeight={96}
          phone
          mentions={mentionable}
          placeholder={draft.scheduled
            ? 'Anything else about it…'
            : `Write a note about ${subject}…`}
        />

        {draft.scheduled && (
          <>
            <label className="flex h-[52px] items-center gap-2 rounded-field border border-line
                              bg-surface px-3 text-cell text-text-muted">
              When
              <input
                type="datetime-local"
                value={draft.dueAt}
                onChange={(event) => draft.setDueAt(event.target.value)}
                className="ml-auto bg-transparent text-right text-body text-text"
              />
            </label>
            <label className="flex h-[52px] items-center gap-2 rounded-field border border-line
                              bg-surface px-3 text-cell text-text-muted">
              Assign to
              <select
                value={draft.assigneeId}
                onChange={(event) => draft.setAssigneeId(event.target.value)}
                className="ml-auto bg-transparent text-right text-body text-text"
              >
                <option value="">Nobody</option>
                {people.map((person) => (
                  <option key={person.id} value={person.id}>{person.name}</option>
                ))}
              </select>
            </label>
          </>
        )}

        {/*
          * Who can see it, in words — the card composer's own two, at 40px.
          * "Internal" and "Crew sees it" are different enough that a switch
          * with one label would leave somebody guessing which way was which.
          */}
        <div
          role="radiogroup"
          aria-label="Who can see it"
          className="flex h-10 overflow-hidden rounded-button border border-line"
        >
          {(['internal', 'crew'] as const).map((value) => (
            <button
              key={value}
              type="button"
              role="radio"
              aria-checked={draft.visibility === value}
              onClick={() => draft.setVisibility(value)}
              className={cn('flex-1 text-cell font-semibold',
                draft.visibility === value ? 'bg-text text-surface' : 'text-text-muted')}
            >
              {value === 'internal' ? 'Internal' : 'Crew sees it'}
            </button>
          ))}
        </div>
      </div>
    </PanelSheet>
    </>
  )
}

/** The three ways a phone has of attaching something. */
export function AttachSheet({ open, subject, onClose, onPick }: {
  open: boolean
  subject: string
  onClose: () => void
  onPick: (files: FileList, options: UploadOptions) => void
}) {
  const [visibility, setVisibility] = useState<FileVisibility>('internal')
  const camera = useRef<HTMLInputElement>(null)
  const library = useRef<HTMLInputElement>(null)
  const anything = useRef<HTMLInputElement>(null)

  const take = (event: { target: HTMLInputElement }) => {
    const { files } = event.target
    if (files && files.length > 0) onPick(files, { visibility })
    event.target.value = ''
    onClose()
  }

  const rows = [
    {
      key: 'camera',
      label: 'Take a photo',
      body: `Straight onto ${subject}.`,
      icon: <IconCamera size={20} />,
      input: camera,
    },
    {
      key: 'library',
      label: 'Photo library',
      body: 'Pick one or many.',
      icon: <IconImage size={20} />,
      input: library,
    },
    {
      key: 'file',
      label: 'Choose a file',
      body: 'A contract, a plan or a PDF from Files.',
      icon: <IconFile size={20} />,
      input: anything,
    },
  ]

  return (
    <PanelSheet
      open={open}
      onClose={onClose}
      title="Add photos or files"
      sub={subject}
      footer={<SheetButton variant="default" onClick={onClose}>Cancel</SheetButton>}
    >
      {/*
        * Three separate inputs rather than one with its attributes swapped.
        * `capture` and `accept` are read when the picker opens, and a React
        * re-render between the tap and the open is not something this can
        * promise — so the camera row is an input that has always been the
        * camera.
        */}
      <input ref={camera} type="file" accept="image/*" capture="environment"
        className="hidden" onChange={take} />
      <input ref={library} type="file" accept="image/*" multiple
        className="hidden" onChange={take} />
      <input ref={anything} type="file" multiple className="hidden" onChange={take} />

      <div className="flex flex-col">
        {rows.map((row) => (
          <button
            key={row.key}
            type="button"
            onClick={() => row.input.current?.click()}
            className="flex min-h-[68px] items-center gap-3 border-b border-line-soft px-4
                       text-left active:bg-hover"
          >
            <span
              aria-hidden
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-tile-large
                         bg-hover text-text-body"
            >
              {row.icon}
            </span>
            <span className="flex min-w-0 flex-1 flex-col gap-0.5">
              <b className="text-[14.5px] font-semibold leading-tight text-text">{row.label}</b>
              <span className="truncate text-cell text-text-muted">{row.body}</span>
            </span>
          </button>
        ))}
      </div>

      <div className="flex flex-col gap-2 p-4">
        <div
          role="radiogroup"
          aria-label="Who can see it"
          className="flex h-10 overflow-hidden rounded-button border border-line"
        >
          {(['internal', 'crew', 'portal'] as const).map((value) => (
            <button
              key={value}
              type="button"
              role="radio"
              aria-checked={visibility === value}
              onClick={() => setVisibility(value)}
              className={cn('flex-1 text-cell font-semibold capitalize',
                visibility === value ? 'bg-text text-surface' : 'text-text-muted')}
            >
              {value}
            </button>
          ))}
        </div>
        <p className="m-0 text-cell leading-[1.45] text-text-muted">
          Internal stays with your team. Crew shows on the visit. Portal shows to the customer.
        </p>
      </div>
    </PanelSheet>
  )
}
